import { Component, OnInit, HostListener } from '@angular/core';
import { Observable } from 'rxjs';
import { UsersState } from '@toro/common/shared/state-management/users/users.reducer';
import { UserProfileState } from '@toro/common/shared/state-management/userprofile/userprofile.reducer';
import { UtilitiesService } from '@toro/common/shared/services/utilities.service';
import { Store } from '@ngrx/store';
import { AppState } from '@toro/common/shared/interfaces/app-state.interface';
import { Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Question } from './models/question';
import { QUESTIONS_LIST, SUITABILITY_DESCRITIONS, CUSTOM_WALLETS_DATASET } from './data';
import { Answer } from './models/answer';
import { QuestionAnswer } from './models/question-answer';
import { getHeroContentImageAccordingWithUserSex } from '../utils';
import { BlipChatControlSingleton } from '@hub-workspace/apps/logged-home/src/app/pages/onboarding/utils';
import { Validators, FormBuilder, FormGroup } from '@angular/forms';

@Component({
  selector: 'app-custom-wallet-builder',
  templateUrl: './custom-wallet-builder.component.html',
  styleUrls: ['./custom-wallet-builder.component.scss']
})
export class CustomWalletBuilderComponent implements OnInit {
  [x: string]: any;
  public showQuiz = false;
  public quizQuestionsList: Question[] = QUESTIONS_LIST;
  public currentQuestion: Question;
  public amount: string;
  public onboardingImage: string;
  public suitabilityDescritionsDict = SUITABILITY_DESCRITIONS;
  public userCanGoToPrevQuestion: boolean = false;
  public userCanGoToNextQuestion: boolean = false;
  public questionAnswers: QuestionAnswer[] = [];
  public currentStep: number = 0;
  public showValueAvailableToInvestForm: boolean = false;
  public valueToInvest: number;
  public canUserBackToApresentation = true;
  public userSuitability = {
    title: null,
    description: null
  };
  public currentUser;
  public investForm = {
    title: 'Quanto você pretende investir hoje?',
    description: `Uma dica: invista a partir de R$ 10.000,00.
                    Nossos especialistas recomendam este valor mínimo para
                    que você possa começar seus investimentos com diversificação.
                    Isso é muito importante para potencializar seus ganhos.`
  };

  protected auth$: Observable<UsersState>;
  protected user$: Observable<UserProfileState>;
  protected customWalletClassificationData = CUSTOM_WALLETS_DATASET;

  constructor(
    public utilitiesService: UtilitiesService,
    private store: Store<AppState>,
    private router: Router,
    public FB: FormBuilder
  ) {
    this.observeDataOnStore();
  }

  public ngOnInit() {
    BlipChatControlSingleton.hideChat();
  }

  @HostListener('window:resize', ['$event'])
  public heroContentImageChangeObserver(event) {
    if (event.target.innerWidth) {
      if (!this.user) return;
      this.onboardingImage = getHeroContentImageAccordingWithUserSex(
        this.user.user
      );
    }
  }

  public showQuizToPersonalizeMyWallet() {
    this.showQuiz = !this.showQuiz;
    this.currentQuestion = this.quizQuestionsList[0];
    this.canUserBackToApresentation = true;
    this.calculateCurrentStepPosition();
  }

  public goToBalanceToInvestForm() {
    this.showValueAvailableToInvestForm = true;
    this.showQuiz = false;
    this.currentStep = this.quizQuestionsList.length + 1;
  }

  public goBackToQuiz() {
    this.showValueAvailableToInvestForm = false;
    this.showQuiz = true;

    this.calculateCurrentStepPosition().goToLastQuestionAnswered();

    this.userCanGoToPrevQuestion = true;
  }

  public goToQuestionAfterAnswerOrEnd(answer: Answer) {
    if (!answer.isEnd) {
      this.setCurrentQuestion(
        this.quizQuestionsList.filter(
          question => question.id === answer.goToQuestion
        )[0]
      );
    } else {
      this.goToBalanceToInvestForm();
    }
    return this;
  }

  public selectAnswer(answer: Answer, hasUserInteraction: boolean = false) {
    this.currentQuestion.answersList.map(answerInQuestion => {
      if (answerInQuestion.id === answer.id && !hasUserInteraction) {
        answerInQuestion.selected = true;
      } else if (
        answerInQuestion.id === answer.id &&
        hasUserInteraction &&
        !answerInQuestion.selected
      ) {
        answerInQuestion.selected = true;
      } else {
        answerInQuestion.selected = false;
      }

      return answerInQuestion;
    });

    // Para liberar o botão na View
    this.canUserGoToNextQuestion();
  }

  public calculateCurrentStepPosition() {
    this.currentStep =
      this.quizQuestionsList
        .map(question => question.id)
        .indexOf(this.currentQuestion.id) + 1;

    return this;
  }

  public goToNextQuestion() {
    if (this.canUserGoToNextQuestion()) {
      const answerSelected = this.getAnswerSelectedByUserForCurrentQuestion();

      this.saveAnswerOfQuestion(answerSelected)
        .verifyIfUserCanGoToPrevQuestion(answerSelected)
        .goToQuestionAfterAnswerOrEnd(answerSelected)
        .calculateCurrentStepPosition()
        .canUserGoToNextQuestion();
    }

    return this;
  }

  public selectLastAnswerSavedByUserInCurrentQuestion(lastAnswerQuestion) {
    this.selectAnswer(
      this.currentQuestion.answersList.filter(
        answer => answer.id === lastAnswerQuestion.answerId
      )[0],
      false
    );

    // Para liberar o botão na View
    this.canUserGoToNextQuestion();

    return this;
  }

  public goToLastQuestionAnswered() {
    const lastAnswerQuestion = this.questionAnswers.pop();
    const isTheFirsQuestionOfTheQuiz =
      this.quizQuestionsList[0].id === this.currentQuestion.id;

    if (isTheFirsQuestionOfTheQuiz) {
      this.showQuiz = false;
    } else {
      this.quizQuestionsList.forEach(question => {
        if (question.id === lastAnswerQuestion.questionId) {
          const answerSelectedInThisQuestion = question.answersList.filter(
            answer => answer.id === lastAnswerQuestion.answerId
          )[0];

          this.setCurrentQuestion(
            question
          ).selectLastAnswerSavedByUserInCurrentQuestion(lastAnswerQuestion);
        }
      });
    }

    return this;
  }

  public goToPrevQuestion() {
    this.goToLastQuestionAnswered().calculateCurrentStepPosition();
  }

  public sendValueToInvestment() {
    let suitability = this.userSuitability.title;
    const valueToInvest = Number(this.amount);

    if (!suitability) {
      suitability = 'conservador';
    } else {
      suitability = String(suitability).toLowerCase();
    }

    const classification = this.classifyUserBySuitabilityAndValueToInvest(
      suitability,
      valueToInvest
    )[0];

    this.router.navigateByUrl(classification.link);
  }

  protected classifyUserBySuitabilityAndValueToInvest(
    suitability,
    valueToInvest
  ) {
    const categoriesFilteredBySuitability = this.customWalletClassificationData.filter(
      category => {
        return category.suitability == suitability;
      }
    );

    const cat = categoriesFilteredBySuitability.filter(category => {
      let minToInvest = category.valueRange[0];
      let limitToInvest = category.valueRange[1];
      let hasTwoValuesToClassify = minToInvest != null && limitToInvest != null;
      let hasLimitAndHasNotMin = minToInvest != null && limitToInvest == null;
      let hasMinAndHasNotLimit = minToInvest == null && limitToInvest != null;

      if (hasTwoValuesToClassify) {
        if (valueToInvest >= minToInvest && valueToInvest <= limitToInvest) {
          return category;
        }
      }

      if (hasLimitAndHasNotMin) {
        if (valueToInvest >= minToInvest) {
          return category;
        }
      }

      if (hasMinAndHasNotLimit) {
        if (valueToInvest <= limitToInvest) {
          return category;
        }
      }
    });

    return cat;
  }

  protected setCurrentQuestion(question: Question) {
    this.currentQuestion = question;

    return this;
  }

  protected getAnswerSelectedByUserForCurrentQuestion(): Answer | null {
    const answersSelected = this.currentQuestion.answersList.filter(
      awnser => awnser.selected
    );

    if (answersSelected.length > 0) {
      return answersSelected[0];
    }
    return null;
  }

  protected canUserGoToNextQuestion() {
    this.userCanGoToNextQuestion = this.getAnswerSelectedByUserForCurrentQuestion()
      ? true
      : false;

    return this.userCanGoToNextQuestion;
  }

  protected verifyIfUserCanGoToPrevQuestion(answerSelected: Answer) {
    this.userCanGoToPrevQuestion =
      this.questionAnswers.filter(qa => answerSelected.id === qa.answerId)
        .length > 0;

    return this;
  }

  protected saveAnswerOfQuestion(answer: Answer, question?: Question) {
    let questionWasAnsweredBefore = false;

    if (!question) {
      question = this.currentQuestion;
    }

    this.questionAnswers.map(qa => {
      if (qa.questionId === question.id) {
        questionWasAnsweredBefore = true;
        qa.answerId = answer.id;
      }
      return qa;
    });

    if (!questionWasAnsweredBefore) {
      this.questionAnswers.push({
        answerId: answer.id,
        questionId: question.id
      });
    }

    return this;
  }

  protected observeDataOnStore() {
    this.auth$ = this.store.select(store => store.authentication);
    this.user$ = this.store.select(store => store.userProfile);

    this.auth$.pipe(filter(data => !!data.currentUser)).subscribe(data => {
      this.currentUser = { ...data.currentUser };
    });

    this.user$.subscribe(user => {
      this.currentUser = user;
      this.onboardingImage = getHeroContentImageAccordingWithUserSex(
        this.currentUser.user
      );
    });

    this.store
      .select(store => store.suitability)
      .pipe()
      .subscribe(data => {
        if (data.profile) {
          this.userSuitability = {
            title: data.profile.description,
            description: this.suitabilityDescritionsDict[
              data.profile.description
            ]
          };
        }
      });
  }
}
